// Breakwater, promenade plate and the sloping sand of the beach.
import * as THREE from 'three';
import { PI } from '../lib/math.js';
import { pick, rnd, rr } from '../lib/random.js';
import { scene } from '../core/scene.js';
import { M, add, canvasTex } from '../lib/factory.js';
import { octPts, slabGeo } from '../lib/shapes.js';
import { walkMat } from '../materials/surfaces.js';
import { ISLAND } from './island.js';
import { beachSlope } from '../rules/beach.js';

export let sandH;

export function buildShore() {
  sandH = function sandH(x, z) {
    const cw = 130 / 120,
      cd = 18 / 24,
      gx = (x + 65) / cw,
      gz = (z - 51) / cd,
      i = Math.floor(gx),
      j = Math.floor(gz),
      fx = gx - i,
      fz = gz - j;
    const h = (a, b) => sandNode(-65 + a * cw, 51 + b * cd);
    // the plane's cells split along the diagonal from (i, j+1) to (i+1, j)
    return fx + fz <= 1
      ? h(i, j) + (h(i + 1, j) - h(i, j)) * fx + (h(i, j + 1) - h(i, j)) * fz
      : h(i + 1, j + 1) +
          (h(i, j + 1) - h(i + 1, j + 1)) * (1 - fx) +
          (h(i + 1, j) - h(i + 1, j + 1)) * (1 - fz);
  };
  // a rock breakwater at the east end of the beach
  {
    const rocks = [];
    for (let k = 0; k < 90; k++) {
      const t = rnd();
      rocks.push({
        x: 47 + rr(-1.6, 1.6),
        z: 56 + t * 26,
        y: -0.55 + rr(0, 0.9) * (1 - t * 0.3),
        s: rr(0.7, 1.6),
        r: [rr(0, PI), rr(0, PI), rr(0, PI)],
      });
    }
    const im = new THREE.InstancedMesh(
        new THREE.DodecahedronGeometry(0.6, 0),
        M(0xffffff, { roughness: 0.95 }),
        rocks.length,
      ),
      o = new THREE.Object3D(),
      c = new THREE.Color();
    rocks.forEach((r, i) => {
      o.position.set(r.x, r.y, r.z);
      o.rotation.set(...r.r);
      o.scale.set(r.s, r.s * rr(0.6, 0.9), r.s);
      o.updateMatrix();
      im.setMatrixAt(i, o.matrix);
      im.setColorAt(i, c.set(pick([0x8f8a82, 0x7a756d, 0xa29c92, 0x6b6660])));
    });
    im.castShadow = im.receiveShadow = true;
    scene.add(im);
  }
  // promenade plate around the Eixample grid, open to the beach in the south
  {
    const W = (x, z) => [x, -z],
      o = new THREE.Shape(),
      R = 14,
      E = ISLAND,
      B = 51;
    o.moveTo(...W(-E, B));
    o.lineTo(...W(-E, -E + R));
    o.quadraticCurveTo(...W(-E, -E), ...W(-E + R, -E));
    o.lineTo(...W(E - R, -E));
    o.quadraticCurveTo(...W(E, -E), ...W(E, -E + R));
    o.lineTo(...W(E, B));
    o.closePath();
    const hole = new THREE.Path(),
      hp = octPts(94.5, 4.8);
    hole.moveTo(...hp[0]);
    for (let i = 1; i < 8; i++) hole.lineTo(...hp[i]);
    hole.closePath();
    o.holes.push(hole);
    add(slabGeo(o, 0.3, 0.06), walkMat, 0, 0, 0);
  }
  // beach: sand sloping into the sea, with a ragged shoreline
  // painted sand: warm base, soft sunlit and shaded blotches, wind ripples, a few shells and pebbles, a light grain
  const sandTex = canvasTex(256, 256, (c) => {
    c.fillStyle = '#edd6a3';
    c.fillRect(0, 0, 256, 256);
    const wrap = (x, y, r, fn) => {
      for (const ox of [-256, 0, 256])
        for (const oy of [-256, 0, 256])
          if (x + ox + r > 0 && x + ox - r < 256 && y + oy + r > 0 && y + oy - r < 256)
            fn(x + ox, y + oy);
    };
    for (let k = 0; k < 26; k++) {
      const x0 = rnd() * 256,
        y0 = rnd() * 256,
        r = rr(24, 70),
        lit = rnd() < 0.55;
      wrap(x0, y0, r, (x, y) => {
        const g = c.createRadialGradient(x, y, 0, x, y, r);
        g.addColorStop(0, lit ? 'rgba(250,236,200,.5)' : 'rgba(218,190,136,.28)');
        g.addColorStop(1, 'rgba(0,0,0,0)');
        c.fillStyle = g;
        c.fillRect(x - r, y - r, 2 * r, 2 * r);
      });
    }
    c.lineCap = 'round';
    for (let k = 0; k < 34; k++) {
      const y1 = rnd() * 256,
        x1 = rnd() * 256,
        len = rr(30, 80),
        a = rr(0.18, 0.32),
        lw = rr(1.2, 2.2);
      wrap(x1 + len / 2, y1, len, (xc, y0) => {
        const x0 = xc - len / 2;
        c.strokeStyle = `rgba(196,162,104,${a})`;
        c.lineWidth = lw;
        c.beginPath();
        for (let x = 0; x <= len; x += 4) {
          const y = y0 + Math.sin((x1 + x) * 0.09) * 3;
          x ? c.lineTo(x0 + x, y) : c.moveTo(x0 + x, y);
        }
        c.stroke();
      });
    }
    for (let k = 0; k < 2200; k++) {
      c.fillStyle = rnd() < 0.5 ? 'rgba(200,170,115,.35)' : 'rgba(255,245,220,.4)';
      c.fillRect(rnd() * 256, rnd() * 256, 1, 1);
    }
    for (let k = 0; k < 14; k++) {
      const x = rnd() * 256,
        y = rnd() * 256;
      c.fillStyle = pick(['#f7efe2', '#e9c9b4', '#b9a78f', '#8f8678']);
      c.beginPath();
      c.ellipse(x, y, rr(1.2, 2.6), rr(1, 1.8), rnd() * PI, 0, PI * 2);
      c.fill();
    }
  });
  sandTex.repeat.set(12, 2);
  // beach surface: the height at a grid node of the sand mesh (130 x 18 units, 120 x 24 cells, from z = 51), and
  // sandH(), the height anywhere on that triangulated surface, so things laid on the sand (towels, props) follow it
  function sandNode(x, z) {
    const t = THREE.MathUtils.clamp((z - 51) / 14, 0, 1);
    let y = beachSlope(z) + Math.sin(x * 0.35) * 0.03 + Math.sin(x * 1.3 + z) * 0.015;
    const ex = Math.max(0, Math.abs(x) - (ISLAND - 14)),
      ez = Math.max(0, z - (ISLAND - 14));
    if (Math.hypot(ex, ez) > 14 - (z - 51) * 0.05) y -= 0.8;
    return y + (Math.sin(x * 0.9) * 0.5 + 0.5) * 0.04 * t;
  }
  {
    const g = new THREE.PlaneGeometry(130, 18, 120, 24).rotateX(-PI / 2),
      pos = g.attributes.position;
    for (let i = 0; i < pos.count; i++) {
      const x = pos.getX(i),
        z = pos.getZ(i) + 60;
      pos.setXYZ(i, x, sandNode(x, z), z);
    }
    // the strip the waves reach is darker, cooler wet sand, with a lighter line where the last wave dried
    const col = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const z = pos.getZ(i) + 60,
        x = pos.getX(i),
        edge = 61.2 + Math.sin(x * 0.21) * 0.5 + Math.sin(x * 0.07 + 1) * 0.6;
      const wet = THREE.MathUtils.smoothstep(z, edge - 1.4, edge + 0.4),
        dry = Math.exp(-(((z - (edge - 2.2)) / 0.35) ** 2)) * 0.08;
      col.set([1 - wet * 0.2 + dry, 1 - wet * 0.26 + dry, 1 - wet * 0.3 + dry], i * 3);
    }
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    g.computeVertexNormals();
    add(
      g,
      M(0xffffff, { map: sandTex, flatShading: false, roughness: 1, vertexColors: true }),
      0,
      0,
      0,
    ).castShadow = false;
    const foamTex = canvasTex(256, 16, (c) => {
      const gr = c.createLinearGradient(0, 0, 0, 16);
      gr.addColorStop(0, 'rgba(255,255,255,0)');
      gr.addColorStop(0.5, 'rgba(255,255,255,.85)');
      gr.addColorStop(1, 'rgba(255,255,255,0)');
      c.fillStyle = gr;
      c.fillRect(0, 0, 256, 16);
    });
    foamTex.repeat.set(10, 1);
    window.foam = add(
      new THREE.PlaneGeometry(120, 1.4).rotateX(-PI / 2),
      new THREE.MeshBasicMaterial({
        map: foamTex,
        transparent: true,
        depthWrite: false,
        opacity: 0.8,
      }),
      0,
      -0.3,
      63.3,
    );
    window.foam.castShadow = window.foam.receiveShadow = false;
  }
}
